import authService, { TOKEN_TYPES } from "./authService";
import { toBackendTrialId } from "@/utils/trialIdMap";
import type {
  CreateMilestonePayload,
  PlanOfRecordMilestoneRow,
  UpdateMilestonePayload,
} from "@/views/PlanOfRecord/planOfRecord.types";

export class PlanOfRecordApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "PlanOfRecordApiError";
    this.status = status;
  }
}

const base = (trialId: string) =>
  `${import.meta.env.VITE_API_URL}/data/trial/${toBackendTrialId(trialId)}/plan_of_record/milestones`;

/**
 * DRF validation errors come back as {field: ["message", ...]}, not {detail: "..."} —
 * reading only `detail` discarded every serializer-worded validation message (e.g. the
 * rationale-length or scope requirements) behind a bare "Request failed (400)".
 */
function extractErrorMessage(payload: unknown, status: number): string {
  if (payload && typeof payload === "object") {
    const record = payload as Record<string, unknown>;
    if (typeof record.detail === "string") return record.detail;
    const fieldErrors = Object.values(record)
      .flat()
      .filter((value): value is string => typeof value === "string");
    if (fieldErrors.length > 0) return fieldErrors.join(" ");
  }
  return `Request failed (${status})`;
}

async function request<T>(
  url: string,
  options: { method?: string; body?: unknown } = {},
): Promise<T> {
  const token = authService.getToken(TOKEN_TYPES.ACCESS) ?? "";
  const response = await fetch(url, {
    method: options.method ?? "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new PlanOfRecordApiError(
      extractErrorMessage(payload, response.status),
      response.status,
    );
  }
  return payload as T;
}

export const planOfRecordService = {
  createMilestone: (trialId: string, payload: CreateMilestonePayload) =>
    request<PlanOfRecordMilestoneRow>(`${base(trialId)}/`, {
      method: "POST",
      body: payload,
    }),

  updateMilestone: (
    trialId: string,
    milestoneId: string,
    payload: UpdateMilestonePayload,
  ) =>
    request<PlanOfRecordMilestoneRow>(`${base(trialId)}/${milestoneId}/`, {
      method: "PATCH",
      body: payload,
    }),

  deleteMilestone: async (
    trialId: string,
    milestoneId: string,
    rationale?: string,
  ): Promise<void> => {
    const token = authService.getToken(TOKEN_TYPES.ACCESS) ?? "";
    const response = await fetch(`${base(trialId)}/${milestoneId}/`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: rationale ? JSON.stringify({ rationale }) : undefined,
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      throw new PlanOfRecordApiError(
        extractErrorMessage(payload, response.status),
        response.status,
      );
    }
  },
};
